import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Box, IconButton, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  CommandBarContainer,
  CommandBarDivider,
} from "../../styled/pos/windows11";
import CompactCustomerInfo from "./customer/CompactCustomerInfo";
import CompactOrderTypeSelector from "./orderType/CompactOrderTypeSelector";
import CompactAddressInfo from "./address/CompactAddressInfo";
import CompactCartSummary from "./cart/CompactCartSummary";
import CompactPaymentButton from "./financial/CompactPaymentButton";
import TutorialTooltip from "../../common/TutorialTooltip";
import { resetNewOrderSlice } from "../../../store/actions/newOrderSlice";
import useNewOrderTooltips from "../../../hooks/useNewOrderTooltips";

const CommandBar = ({ tableInfo, submitOrder }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const theme = useTheme();
  const showLabels = useMediaQuery(theme.breakpoints.up("md"));
  const {
    cartRef,
    paymentRef,
    cartAnchor,
    showCartTooltip,
    handleCloseCartTooltip,
  } = useNewOrderTooltips();

  // Anchors for validation alert tooltips
  const [customerAnchor, setCustomerAnchor] = useState(null);
  const [orderTypeAnchor, setOrderTypeAnchor] = useState(null);
  const [addressAnchor, setAddressAnchor] = useState(null);

  // Validation alert state
  const [alertAnchor, setAlertAnchor] = useState(null);
  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const alertTimerRef = useRef(null);

  const handleValidationFail = useCallback(
    (field, message) => {
      clearTimeout(alertTimerRef.current);

      const anchorMap = {
        orderType: orderTypeAnchor,
        customer: customerAnchor,
        address: addressAnchor,
      };

      setAlertAnchor(anchorMap[field] || null);
      setAlertMessage(message);
      setShowAlert(true);
      alertTimerRef.current = setTimeout(() => setShowAlert(false), 4000);
    },
    [customerAnchor, orderTypeAnchor, addressAnchor]
  );

  const handleCloseAlert = useCallback(() => {
    setShowAlert(false);
    clearTimeout(alertTimerRef.current);
  }, []);

  useEffect(() => {
    return () => clearTimeout(alertTimerRef.current);
  }, []);

  const handleBack = () => {
    dispatch(resetNewOrderSlice());
    history.push("/central-delivery");
  };

  return (
    <CommandBarContainer>
      {/* Back Button */}
      <Tooltip title="Voltar" arrow>
        <IconButton
          onClick={handleBack}
          size="medium"
          aria-label="Voltar"
          sx={{
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            "&:hover": {
              bgcolor: "action.hover",
              borderColor: "primary.main",
            },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
      </Tooltip>
      <CommandBarDivider />

      {/* Customer Info (hidden for table orders) */}
      {!tableInfo && (
        <>
          <Box ref={setCustomerAnchor} sx={{ display: "flex" }}>
            <CompactCustomerInfo iconOnly={!showLabels} />
          </Box>
          <CommandBarDivider />
        </>
      )}

      {/* Order Type Selector (hidden for table orders) */}
      {!tableInfo && (
        <>
          <Box ref={setOrderTypeAnchor} sx={{ display: "flex" }}>
            <CompactOrderTypeSelector iconOnly={!showLabels} />
          </Box>
          <CommandBarDivider />
        </>
      )}

      {/* Address Info (only for delivery orders, hidden for table orders) */}
      {!tableInfo && (
        <Box ref={setAddressAnchor} sx={{ display: "flex" }}>
          <CompactAddressInfo iconOnly={!showLabels} />
        </Box>
      )}

      {/* Cart Summary (always visible) */}
      <CompactCartSummary containerRef={cartRef} iconOnly={!showLabels} />

      {/* Payment Button — always full-width bottom row, shown only when ready */}
      <Box sx={{ display: "flex", width: "100%", flexBasis: "100%" }}>
        <CompactPaymentButton
          containerRef={paymentRef}
          onCreateOrder={submitOrder}
          onValidationFail={handleValidationFail}
        />
      </Box>

      {/* Onboarding Tooltips */}
      <TutorialTooltip
        open={showCartTooltip}
        anchorEl={cartAnchor}
        message="Produto adicionado ao carrinho!"
        emoji="🛒"
        color="primary"
        placement="bottom"
        delay={300}
        onClose={handleCloseCartTooltip}
      />
      {/* Validation Alert Tooltip */}
      <TutorialTooltip
        open={showAlert}
        anchorEl={alertAnchor}
        message={alertMessage}
        emoji="⚠️"
        color="warning"
        placement="bottom"
        delay={0}
        onClose={handleCloseAlert}
      />
    </CommandBarContainer>
  );
};

export default CommandBar;
